import { STATUS } from "@/constants/statuses";
import { OffsetPaginationMetaEntity } from "@/entities/OffsetPaginationEntity";
import {
    DesignFeedApiService,
    GetMediaListQueryParams,
    ReactionPayload,
} from "@apps/iloom_customization/design_feed/api_services/DesignFeedApiService";
import MediaListStore from "@apps/iloom_customization/design_feed/stores/MediaListStore";
import { iloomCustomizationApplicationLoggerToken } from "@apps/iloom_customization/tokens";
import {
    PaginationMeta,
    QueryPaginationMeta,
} from "@apps/iloom_customization/types/PaginationMeta";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import { applyTransaction } from "@datorama/akita";
import { omit } from "rambda";
import { catchError, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const transformMeta = (meta: OffsetPaginationMetaEntity): PaginationMeta => {
    return {
        limit: meta.perPage,
        offset: meta.currentOffset,
        total: meta.totalCount,
    };
};

@singleton()
export class DesignFeedService {
    constructor(
        private readonly mediaListStore: MediaListStore,
        private readonly designFeedApiService: DesignFeedApiService,
        private readonly wishlistProductStatusesService: WishlistProductStatusesService,

        @inject(iloomCustomizationApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getMediaList(queryParams: GetMediaListQueryParams) {
        this.mediaListStore.update((state) => {
            if (queryParams.offset) {
                state.loadMoreStatus = STATUS.LOADING;
            } else {
                state.status = STATUS.LOADING;
            }
        });

        return this.designFeedApiService.getMediaList(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "DesignFeedService.getMediaList is errored",
                    error
                );
                this.mediaListStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    item.products.forEach((product) => {
                        this.wishlistProductStatusesService.addProductIdForFetching(
                            product.productId
                        );
                    });
                });
            }),
            tap((value) => {
                this.logger.debug("DesignFeedService.getMediaList is ready");
                applyTransaction(() => {
                    this.mediaListStore.update((state) => {
                        const meta = transformMeta(omit(["items"], value));
                        const items = queryParams.offset
                            ? [...state.mediaList, ...value.items]
                            : value.items;

                        state.meta = meta;
                        state.mediaList = items;
                        state.status = STATUS.READY;
                        state.loadMoreStatus = STATUS.READY;
                    });
                });
            })
        );
    }

    getCustomerMediaList(queryParams: QueryPaginationMeta, isLoadMore = false) {
        this.mediaListStore.update((state) => {
            if (isLoadMore && queryParams.offset) {
                state.loadMoreStatus = STATUS.LOADING;
            } else {
                state.status = STATUS.LOADING;
                state.meta.offset = queryParams.offset;
            }
        });

        return this.designFeedApiService.getCustomerMediaList(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "DesignFeedService.getCustomerMediaList is errored",
                    error
                );
                this.mediaListStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    item.products.forEach((product) => {
                        this.wishlistProductStatusesService.addProductIdForFetching(
                            product.productId
                        );
                    });
                });
            }),
            tap((value) => {
                this.logger.debug(
                    "DesignFeedService.getCustomerMediaList is ready"
                );
                applyTransaction(() => {
                    this.mediaListStore.update((state) => {
                        const meta = transformMeta(omit(["items"], value));
                        const items =
                            isLoadMore && queryParams.offset
                                ? [...state.mediaList, ...value.items]
                                : value.items;

                        state.meta = meta;
                        state.mediaList = items;
                        state.status = STATUS.READY;
                        state.loadMoreStatus = STATUS.READY;
                    });
                });
            })
        );
    }

    addReaction(payload: ReactionPayload) {
        return this.designFeedApiService.addReaction(payload).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "DesignFeedService.addReaction is errored",
                    error
                );
                throw error;
            }),
            tap(() => {
                applyTransaction(() => {
                    this.mediaListStore.update((state) => {
                        state.mediaList = state.mediaList.map((media) => {
                            return {
                                ...media,
                                products: media.products.map((product) => {
                                    if (
                                        product.productId === payload.productId
                                    ) {
                                        return {
                                            ...product,
                                            didViewerReact: true,
                                        };
                                    }
                                    return product;
                                }),
                            };
                        });
                    });
                });
            })
        );
    }

    addFave(mediaId: string) {
        this.mediaListStore.update((state) => {
            state.mediaList = state.mediaList.map((media) => {
                if (media.mediaId === mediaId) {
                    return {
                        ...media,
                        didViewerFave: true,
                    };
                }

                return media;
            });
        });

        return this.designFeedApiService.addFave(mediaId).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "DesignFeedService.addFave is errored",
                    error
                );
                throw error;
            }),
            tap(() => {
                applyTransaction(() => {
                    this.mediaListStore.update((state) => {
                        state.mediaList = state.mediaList.map((media) => {
                            if (media.mediaId === mediaId) {
                                return {
                                    ...media,
                                    didViewerFave: true,
                                };
                            }

                            return media;
                        });
                    });
                });
            })
        );
    }

    removeFave(mediaId: string) {
        applyTransaction(() => {
            this.mediaListStore.update((state) => {
                state.mediaList.map((media) => {
                    if (media.mediaId === mediaId) {
                        return {
                            ...media,
                            didViewerFave: false,
                        };
                    }

                    return media;
                });
            });
        });

        return this.designFeedApiService.removeFave(mediaId).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "DesignFeedService.removeFave is errored",
                    error
                );
                throw error;
            }),
            tap(() => {
                applyTransaction(() => {
                    this.mediaListStore.update((state) => {
                        state.mediaList = state.mediaList.map((media) => {
                            if (media.mediaId === mediaId) {
                                return {
                                    ...media,
                                    didViewerFave: false,
                                };
                            }

                            return media;
                        });
                    });
                });
            })
        );
    }
}
